<template>
  <div class="VikingSnakeGame">
    <div class="VikingSnakeGame__instructions">
      <p v-if="!gameOver">Click the game area and use arrow keys to guide the Viking snake to the treasure! 🗡️</p>
      <p v-else>Click "Play Again" to restart the game!</p>
    </div>
    
    <div class="VikingSnakeGame__game-container" ref="gameContainer">
      <button 
        @click="closeGame" 
        class="VikingSnakeGame__close-button"
        title="Close game and return to preview"
      >
        ✕
      </button>
      
      <canvas 
        ref="gameCanvas" 
        class="VikingSnakeGame__canvas"
        @keydown="handleKeyPress"
        @click="focusCanvas"
        tabindex="0"
      ></canvas>
      
      <div v-if="gameOver" class="VikingSnakeGame__game-over">
        <h4>Game Over!</h4>
        <p>Final Score: {{ score }}</p>
        <v-btn @click="restartGame" color="y1" small>
          Play Again
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VikingSnakeGame',
  data() {
    return {
      canvas: null,
      ctx: null,
      snake: [{ x: 10, y: 10 }],
      food: { x: 15, y: 15 },
      direction: 'right',
      nextDirection: 'right',
      score: 0,
      gameOver: false,
      gameLoop: null,
      gridSize: 25,
      canvasSize: 400,
      containerSize: 0,
      resizeTimeout: null
    }
  },
  mounted() {
    this.initGame()
    this.$nextTick(() => {
      this.$refs.gameCanvas.focus()
    })
    
    // Add resize listener
    window.addEventListener('resize', this.handleResize)
    
    // Add global key listener as backup
    window.addEventListener('keydown', this.handleGlobalKeyPress)
  },
  beforeDestroy() {
    if (this.gameLoop) {
      clearInterval(this.gameLoop)
    }
    // Remove resize listener
    window.removeEventListener('resize', this.handleResize)
    // Remove global key listener
    window.removeEventListener('keydown', this.handleGlobalKeyPress)
  },
  methods: {
    initGame() {
      this.canvas = this.$refs.gameCanvas
      this.ctx = this.canvas.getContext('2d')
      
      // Calculate responsive canvas size
      const container = this.$refs.gameContainer
      const containerWidth = container.clientWidth
      const containerHeight = container.clientHeight
      
      // Use more of the available space, prefer width for wider canvas
      const maxSize = Math.max(containerWidth, containerHeight) * 0.95
      this.canvasSize = Math.min(maxSize, Math.min(containerWidth, containerHeight) * 1.2)
      
      // Handle high DPI displays for crisp rendering
      const devicePixelRatio = window.devicePixelRatio || 1
      const rect = this.canvas.getBoundingClientRect()
      
      // Set the canvas size in CSS pixels
      this.canvas.style.width = this.canvasSize + 'px'
      this.canvas.style.height = this.canvasSize + 'px'
      
      // Set the canvas size in actual pixels
      this.canvas.width = this.canvasSize * devicePixelRatio
      this.canvas.height = this.canvasSize * devicePixelRatio
      
      // Scale the drawing context so everything draws at the correct size
      this.ctx.scale(devicePixelRatio, devicePixelRatio)
      
      this.startGame()
    },
    
    startGame() {
      this.snake = [{ x: 10, y: 10 }]
      this.food = this.generateFood()
      this.direction = 'right'
      this.nextDirection = 'right'
      this.score = 0
      this.gameOver = false
      
      if (this.gameLoop) {
        clearInterval(this.gameLoop)
      }
      
      this.gameLoop = setInterval(() => {
        this.updateGame()
      }, 150)
      
      // Ensure canvas gets focus when game starts
      this.$nextTick(() => {
        this.$refs.gameCanvas.focus()
      })
    },
    
    restartGame() {
      this.startGame()
    },
    
    generateFood() {
      const x = Math.floor(Math.random() * this.gridSize)
      const y = Math.floor(Math.random() * this.gridSize)
      return { x, y }
    },
    
    updateGame() {
      if (this.gameOver) return
      
      // Update direction
      this.direction = this.nextDirection
      
      // Move snake
      const head = { ...this.snake[0] }
      
      switch (this.direction) {
        case 'up':
          head.y--
          break
        case 'down':
          head.y++
          break
        case 'left':
          head.x--
          break
        case 'right':
          head.x++
          break
      }
      
      // Check collision with walls
      if (head.x < 0 || head.x >= this.gridSize || head.y < 0 || head.y >= this.gridSize) {
        this.gameOver = true
        return
      }
      
      // Check collision with self
      if (this.snake.some(segment => segment.x === head.x && segment.y === head.y)) {
        this.gameOver = true
        return
      }
      
      // Add new head
      this.snake.unshift(head)
      
      // Check if food is eaten
      if (head.x === this.food.x && head.y === this.food.y) {
        this.score += 10
        this.food = this.generateFood()
      } else {
        // Remove tail if no food eaten
        this.snake.pop()
      }
      
      this.drawGame()
    },
    
    drawGame() {
      // Clear canvas
      this.ctx.fillStyle = '#2e2c42'
      this.ctx.fillRect(0, 0, this.canvasSize, this.canvasSize)
      
      // Draw grid
      this.ctx.strokeStyle = '#3a3a5a'
      this.ctx.lineWidth = 1
      for (let i = 0; i <= this.gridSize; i++) {
        this.ctx.beginPath()
        this.ctx.moveTo(i * (this.canvasSize / this.gridSize), 0)
        this.ctx.lineTo(i * (this.canvasSize / this.gridSize), this.canvasSize)
        this.ctx.stroke()
        
        this.ctx.beginPath()
        this.ctx.moveTo(0, i * (this.canvasSize / this.gridSize))
        this.ctx.lineTo(this.canvasSize, i * (this.canvasSize / this.gridSize))
        this.ctx.stroke()
      }
      
      // Draw snake with better visibility
      this.snake.forEach((segment, index) => {
        if (index === 0) {
          // Draw head with Viking helmet - bright yellow
          this.ctx.fillStyle = '#FFB800'
        } else {
          // Draw body - bright orange
          this.ctx.fillStyle = '#FF6B00'
        }
        
        const cellSize = this.canvasSize / this.gridSize
        const padding = 2
        
        this.ctx.fillRect(
          segment.x * cellSize + padding,
          segment.y * cellSize + padding,
          cellSize - (padding * 2),
          cellSize - (padding * 2)
        )
        
        // Add border for better visibility
        this.ctx.strokeStyle = '#FFD700'
        this.ctx.lineWidth = 1
        this.ctx.strokeRect(
          segment.x * cellSize + padding,
          segment.y * cellSize + padding,
          cellSize - (padding * 2),
          cellSize - (padding * 2)
        )
      })
      
      // Draw food (treasure) - bright gold
      this.ctx.fillStyle = '#FFD700'
      this.ctx.beginPath()
      const cellSize = this.canvasSize / this.gridSize
      this.ctx.arc(
        this.food.x * cellSize + cellSize / 2,
        this.food.y * cellSize + cellSize / 2,
        cellSize / 4,
        0,
        2 * Math.PI
      )
      this.ctx.fill()
      
      // Add glow effect to food
      this.ctx.shadowColor = '#FFD700'
      this.ctx.shadowBlur = 10
      this.ctx.fill()
      this.ctx.shadowBlur = 0
    },
    
    handleKeyPress(event) {
      this.handleArrowKeys(event)
    },
    
    handleGlobalKeyPress(event) {
      // Only handle arrow keys if the game is active
      if (this.gameOver) return
      
      // Check if the event is from the canvas
      if (event.target === this.$refs.gameCanvas) return
      
      this.handleArrowKeys(event)
    },
    
    handleArrowKeys(event) {
      // Prevent default behavior for arrow keys
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
        event.preventDefault()
      }
      
      switch (event.key) {
        case 'ArrowUp':
          if (this.direction !== 'down') {
            this.nextDirection = 'up'
          }
          break
        case 'ArrowDown':
          if (this.direction !== 'up') {
            this.nextDirection = 'down'
          }
          break
        case 'ArrowLeft':
          if (this.direction !== 'right') {
            this.nextDirection = 'left'
          }
          break
        case 'ArrowRight':
          if (this.direction !== 'left') {
            this.nextDirection = 'right'
          }
          break
      }
    },
    
    focusCanvas() {
      this.$refs.gameCanvas.focus()
    },
    
    handleResize() {
      // Debounce resize events
      clearTimeout(this.resizeTimeout)
      this.resizeTimeout = setTimeout(() => {
        this.initGame()
      }, 100)
    },

    closeGame() {
      this.$emit('close-game')
    }
  }
}
</script>

<style lang="scss" scoped>
.VikingSnakeGame {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  background: linear-gradient(135deg, #2e2c42 0%, #1a1a2e 100%);
  color: white;
  padding: 20px;
  box-sizing: border-box;
  
  &__instructions {
    text-align: center;
    color: #a0a0a0;
    font-size: 14px;
    margin-bottom: 20px;
    z-index: 2;
    
    p {
      margin: 0;
      line-height: 1.4;
    }
  }
  
  &__game-container {
    position: relative;
    width: 100%;
    height: calc(100% - 60px);
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
  }

  &__close-button {
    position: absolute;
    top: 10px;
    right: 10px;
    background-color: rgba(58, 58, 90, 0.9);
    border: 1px solid #5a5a7a;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 10;
    transition: all 0.2s ease;
    color: #a0a0a0;

    &:hover {
      background-color: rgba(255, 107, 0, 0.9);
      border-color: #FFB800;
      color: white;
      transform: scale(1.1);
    }
    
    &:focus {
      outline: none;
      box-shadow: 0 0 0 2px rgba(255, 184, 0, 0.5);
    }
  }
  
  &__canvas {
    display: block;
    background: #2e2c42;
    cursor: pointer;
    outline: none;
    max-width: 100%;
    max-height: 100%;
    width: 100%;
    height: 100%;
    border: 2px solid transparent;
    transition: border-color 0.2s ease;
    
    &:focus {
      border-color: #FFB800;
      outline: none;
    }
    
    &:hover {
      border-color: #FF6B00;
    }
  }
  
  &__game-over {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: rgba(46, 44, 66, 0.95);
    backdrop-filter: blur(10px);
    padding: 24px;
    border-radius: 8px;
    text-align: center;
    border: 1px solid #3a3a5a;
    
    h4 {
      margin: 0 0 8px 0;
      color: #FF6B00;
    }
    
    p {
      margin: 0 0 16px 0;
      color: #FFB800;
    }
  }
}
</style> 